import {
  Table,
  Switch,
  Tooltip,
  Modal,
  Button,
  Space,
  Typography,
  Divider,
  Badge,
  Input,
  List,
  Spin,
  Select,
} from "antd";
import { SettingOutlined, SearchOutlined, HolderOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import { getColumnSearchProps } from "../../../utils/tables";
import Header from "../../../components/HeaderList";
import styles from "../styles/SetmenuUI.module.css";

const { Text } = Typography;

const SetmenuUI = (props) => {
  const {
    loading,
    condimentsLoading,
    groups,
    condiments,
    selectedGroupId,
    onGroupSelect,
    confirmVisibility,
    selectedChannelId,
    onChannelChange,
    handleDragDropSku,
    customizableChannels,
    loadingCustomizable,
    savingCustomizable,
    fetchCustomizableChannels,
    onSaveCustomizableChannels,
  } = props;

  const [rulesModalVisible, setRulesModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [groupSearch, setGroupSearch] = useState("");
  const [draggedSku, setDraggedSku] = useState(null);
  const [draggedOverSku, setDraggedOverSku] = useState(null);

  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [tempCustomizableChannels, setTempCustomizableChannels] = useState([]);

  useEffect(() => {
    if (customizableChannels) {
      setTempCustomizableChannels(customizableChannels);
    }
  }, [customizableChannels]);

  const handleOpenConfigModal = () => {
    setConfigModalVisible(true);
    fetchCustomizableChannels();
  };

  const handleToggleChannelCustomizable = (channelId, checked) => {
    setTempCustomizableChannels(prev =>
      prev.map(ch => ch.id === channelId ? { ...ch, isCustomizable: checked } : ch)
    );
  };

  const handleSaveConfig = async () => {
    const activeIds = tempCustomizableChannels
      .filter(ch => ch.isCustomizable)
      .map(ch => ch.id);
    const success = await onSaveCustomizableChannels(activeIds);
    if (success) {
      setConfigModalVisible(false);
    }
  };

  const currentProduct = selectedRecord
    ? condiments.find((p) => p.sku === selectedRecord.sku)
    : null;

  const filteredGroups = groups.filter((g) =>
    String(g.name).toLowerCase().includes(groupSearch.toLowerCase()) ||
    String(g.id).includes(groupSearch)
  );

  // ---------- Admin columns ----------
  const columnsAdmin = [
    {
      title: "SKU",
      dataIndex: "sku",
      ...getColumnSearchProps("sku"),
    },
    {
      title: "Nombre",
      dataIndex: "name",
      ...getColumnSearchProps("name"),
    },
    {
      title: "Disponibilidad",
      align: "center",
      render: (_, record) => (
        <Space direction="vertical" align="center" size={2}>
          <Space size={4}>
            {record?.channels?.map(p => (
              <Tooltip key={p.name} title={`${p.name.toUpperCase()}: ${p.active ? 'Activo' : 'Inactivo'}`}>
                <Badge color={p.active ? 'green' : 'red'} />
              </Tooltip>
            ))}
          </Space>
          <Button
            type="primary"
            icon={<SettingOutlined />}
            onClick={() => { setSelectedRecord(record); setRulesModalVisible(true); }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            Configurar
          </Button>
        </Space>
      ),
    },
  ];

  if (selectedChannelId) {
    columnsAdmin.unshift({
      title: "Orden",
      align: "center",
      width: 80,
      render: () => (
        <span className={styles.dragger}>
          <HolderOutlined />
        </span>
      ),
    });
  }

  const showPagination = condiments.length > 50;

  return (
    <div className={styles.view}>
      <Header
        title="Combos"
        back="/productos"
      />

      {/* Rules modal */}
      <Modal
        title={`Reglas de Disponibilidad - ${currentProduct?.name || ""}`}
        visible={rulesModalVisible}
        onCancel={() => { setRulesModalVisible(false); setSelectedRecord(null); }}
        footer={[
          <Button key="close" type="primary" onClick={() => { setRulesModalVisible(false); setSelectedRecord(null); }}>
            Cerrar
          </Button>,
        ]}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          <Text type="secondary">
            Activa o desactiva la disponibilidad de esta opción de combo en los siguientes canales a nivel central:
          </Text>
          <Divider style={{ margin: "10px 0" }} />
          {currentProduct?.channels?.map((item) => (
            <Space key={item.channel_id} align="center" style={{ justifyContent: "space-between", width: "100%" }}>
              <Text strong>{item.name[0].toUpperCase() + item.name.slice(1).toLowerCase()}</Text>
              <Switch
                checkedChildren="Si"
                unCheckedChildren="No"
                checked={item.active === 1}
                onChange={(checked) =>
                  confirmVisibility(currentProduct?.sku, item.name.toLowerCase(), !checked, currentProduct?.group_id)
                }
              />
            </Space>
          ))}
        </div>
      </Modal>

      {/* Two-panel layout */}
      <div className={styles.layout}>
        <div className={styles.groups}>
          <div style={{ padding: "10px 12px", borderBottom: "1px solid #e8e8e8" }}>
            <Input
              placeholder="Buscar combo..."
              prefix={<SearchOutlined />}
              value={groupSearch}
              onChange={(e) => setGroupSearch(e.target.value)}
              size="small"
            />
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                <Spin />
              </div>
            ) : (
              <List
                dataSource={filteredGroups}
                renderItem={(group) => (
                  <List.Item
                    key={group.id}
                    onClick={() => onGroupSelect(group.id)}
                    style={{
                      cursor: "pointer",
                      padding: "10px 14px",
                      background: String(selectedGroupId) === String(group.id) ? "#fff1f0" : "transparent",
                      borderLeft: String(selectedGroupId) === String(group.id) ? "3px solid #ff4d4f" : "3px solid transparent",
                      transition: "background 0.15s",
                    }}
                  >
                    <List.Item.Meta
                      title={
                        <Text strong style={{ fontSize: 13 }}>
                          {group.id} — {group.name}
                        </Text>
                      }
                      description={
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          No. opciones: {group.condiment_count}
                        </Text>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </div>
        </div>

        {/* Right panel: combos options table */}
        <div className={styles.condiments} >
          {
            selectedGroupId ? (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                  <Text strong style={{ fontSize: 15 }}>
                    {groups.find(g => String(g.id) === String(selectedGroupId))?.name || `Combo ${selectedGroupId}`}
                  </Text>

                  <Space>
                    {condiments.length > 0 && (
                      <Space>
                        <Text type="secondary">Ordenar por Canal:</Text>
                        <Select
                          style={{ width: 180 }}
                          placeholder="Canal para ordenar"
                          value={selectedChannelId}
                          onChange={onChannelChange}
                        >
                          <Select.Option value={null}>Por Defecto</Select.Option>
                          {(condiments[0]?.channels || []).map(ch => (
                            <Select.Option key={ch.channel_id} value={ch.channel_id}>
                              {ch.name.toUpperCase()}
                            </Select.Option>
                          ))}
                        </Select>
                      </Space>
                    )}
                    <Tooltip title="Habilitar/Deshabilitar Canales Personalizables">
                      <Button
                        icon={<SettingOutlined />}
                        onClick={handleOpenConfigModal}
                      />
                    </Tooltip>
                  </Space>
                </div>
                <Table
                  size="small"
                  columns={columnsAdmin}
                  dataSource={condiments}
                  rowKey="id"
                  loading={condimentsLoading}
                  pagination={{ pageSize: 50, showSizeChanger: false, position: [showPagination ? 'bottomRight' : 'none'] }}
                  onRow={(record) => {
                    const draggable = !!selectedChannelId;
                    const isDragging = record.sku === draggedSku;
                    const isDragOver = record.sku === draggedOverSku;

                    let rowClass = "";
                    if (isDragging) rowClass = styles.rowDragging;
                    else if (isDragOver && draggable) rowClass = styles.rowDragOver;

                    return {
                      draggable: draggable,
                      className: rowClass,
                      style: { cursor: draggable ? "grab" : "default" },
                      onDragStart: (event) => {
                        if (!draggable) {
                          event.preventDefault();
                          return;
                        }
                        setDraggedSku(record.sku);
                        event.dataTransfer.setData("draggedSku", record.sku);
                        event.dataTransfer.effectAllowed = "move";
                      },
                      onDragOver: (event) => {
                        if (!draggable) {
                          event.preventDefault();
                          return;
                        }
                        event.preventDefault();
                        if (draggedOverSku !== record.sku) {
                          setDraggedOverSku(record.sku);
                        }
                      },
                      onDragLeave: () => {
                        if (draggedOverSku === record.sku) {
                          setDraggedOverSku(null);
                        }
                      },
                      onDragEnd: () => {
                        setDraggedSku(null);
                        setDraggedOverSku(null);
                      },
                      onDrop: (event) => {
                        if (!draggable) {
                          event.preventDefault();
                          return;
                        }
                        const draggedSkuData = event.dataTransfer.getData("draggedSku");
                        const targetSku = record.sku;
                        setDraggedSku(null);
                        setDraggedOverSku(null);
                        if (draggedSkuData && targetSku && draggedSkuData !== targetSku) {
                          handleDragDropSku(draggedSkuData, targetSku);
                        }
                      },
                    };
                  }}
                />
              </>
            ) : (
              <Text type="secondary">Selecciona un combo para ver sus opciones.</Text>
            )
          }
        </div>
      </div>

      {/* Modal de configuración de canales configurables */}
      <Modal
        title="Configurar Canales Disponibles"
        visible={configModalVisible}
        onCancel={() => setConfigModalVisible(false)}
        onOk={handleSaveConfig}
        confirmLoading={savingCustomizable}
        destroyOnClose
        okText="Guardar"
        cancelText="Cancelar"
        width={450}
      >
        <div style={{ marginBottom: 20 }}>
          <Text type="secondary">
            Selecciona qué canales podrán ser personalizados de forma individual (estado activo y orden de opciones) en esta pantalla. Los canales no seleccionados se ocultarán de las opciones de combo.
          </Text>
        </div>
        {loadingCustomizable ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <Spin size="large" />
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {tempCustomizableChannels.map(ch => (
              <div key={ch.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f9f9f9", borderRadius: "6px" }}>
                <Text strong style={{ fontSize: 14 }}>{ch.name.toUpperCase()}</Text>
                <Switch
                  checkedChildren="Si"
                  unCheckedChildren="No"
                  checked={ch.isCustomizable}
                  onChange={(checked) => handleToggleChannelCustomizable(ch.id, checked)}
                />
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SetmenuUI;
